import { EContentService } from "../../../globals/services/enhancedContent.service";
import { UsersService } from "../../../globals/services/users.service";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import folderIcon from "../../../assets/images/newFolderIcon.svg";
import searchIcon from "../../../assets/images/search-icon.png";
import styles from './Folder.module.scss';
import { FOLDER_PAGE_SIZE } from "../constants";
import ListFilter from "../components/ListFilter";
import AdvancedFiltersDropdown from "../components/AdvancedFiltersDropdown";
const moment = require("moment").default || require("moment");

export const FolderList = (props) => {
    const { navigateToFolderPage, adminUsers, templateList, seminarList, onCountUpdate, currentPage, setCurrentPage } = props;
    const eContentService = EContentService();
    const usersService = UsersService();
    const [list, setList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [nameFilter, setNameFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [createdDateFilter, setCreatedDateFilter] = useState("All");
    const [createdByFilter, setCreatedByFilter] = useState("All");
    const [relatedSeminarFilter, setRelatedSeminarFilter] = useState("All");
    const [relatedTemplateFilter, setRelatedTemplateFilter] = useState("All");
    const [prevFilters, setPrevFilters] = useState({
        name: "",
        status: "All",
        createdDate: "All",
        createdBy: "All",
        relatedSeminar: "All",
        relatedTemplate: "All"
    });
    const [isFiltersDropdownOpen, setIsFiltersDropdownOpen] = useState(false);
    const [availableCreatedBy, setAvailableCreatedBy] = useState([]);
    const [userIdToNameMap, setUserIdToNameMap] = useState({});

    const { me } = useSelector((state) => state.auth);

    // Extract unique created dates from folders
    const availableCreatedDates = useMemo(() => {
        const dates = list
            .map(folder => folder.created)
            .filter(date => date && date.trim() !== "")
            .map(date => moment(date).format("MMMM D, YYYY"))
            .filter((date, index, arr) => arr.indexOf(date) === index)
            .sort((a, b) => moment(b, "MMMM D, YYYY").diff(moment(a, "MMMM D, YYYY")));
        return dates;
    }, [list]);

    // Extract unique seminars from folders
    const availableRelatedSeminars = useMemo(() => {
        const seminars = list
            .flatMap(folder => folder.seminars || [])
            .map(seminar => seminar.SeminarTitle)
            .filter(title => title && title.trim() !== "")
            .filter((title, index, arr) => arr.indexOf(title) === index)
            .sort();
        return seminars;
    }, [list]);

    // Extract unique templates from folders
    const availableRelatedTemplates = useMemo(() => {
        const templateIds = list
            .flatMap(folder => folder.templates || [])
            .filter((templateId, index, arr) => arr.indexOf(templateId) === index);

        return templateIds
            .map(templateId => {
                const template = templateList?.find(t => t._id === templateId);
                return template ? template.title : null;
            })
            .filter(title => title)
            .sort();
    }, [list, templateList]);

    // Extract unique user IDs from folders
    const uniqueUserIds = useMemo(() => {
        return list
            .map(folder => folder.createdBy)
            .filter(userId => userId && userId.trim() !== "")
            .filter((userId, index, arr) => arr.indexOf(userId) === index);
    }, [list]);

    const getCurrentPageItems = () => {
        const startIndex = (currentPage - 1) * FOLDER_PAGE_SIZE;
        const endIndex = startIndex + FOLDER_PAGE_SIZE;
        return filteredList.slice(startIndex, endIndex);
    };

    // Fetch user data for created by filter
    useEffect(() => {
        if (uniqueUserIds.length > 0) {
            usersService.getUserByArrayOfIds(uniqueUserIds)
                .then(users => {
                    const nameMap = {};
                    const userNames = [];

                    users.forEach(user => {
                        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user._id;
                        nameMap[user._id] = fullName;
                        userNames.push(fullName);
                    });

                    setUserIdToNameMap(nameMap);
                    setAvailableCreatedBy(userNames.sort());
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                    setAvailableCreatedBy([]);
                    setUserIdToNameMap({});
                });
        } else {
            setAvailableCreatedBy([]);
            setUserIdToNameMap({});
        }
    }, [uniqueUserIds]);

    const applyFilters = useCallback(() => {
        return list.filter(folder => {
            const matchesName = nameFilter ? folder.title.toLowerCase().includes(nameFilter.toLowerCase()) : true;
            const matchesStatus = statusFilter === "All" || folder.status === statusFilter;

            // Created date filter logic
            let matchesCreatedDate = true;
            if (createdDateFilter !== "All" && folder.created) {
                const folderDate = moment(folder.created).format("MMMM D, YYYY");
                matchesCreatedDate = folderDate === createdDateFilter;
            }

            // Created by filter logic
            let matchesCreatedBy = true;
            if (createdByFilter !== "All" && createdByFilter) {
                const selectedUserId = Object.keys(userIdToNameMap).find(
                    userId => userIdToNameMap[userId] === createdByFilter
                );
                matchesCreatedBy = folder.createdBy === selectedUserId;
            }

            // Related seminar filter logic
            let matchesRelatedSeminar = true;
            if (relatedSeminarFilter !== "All") {
                const folderSeminars = folder.seminars || [];
                matchesRelatedSeminar = folderSeminars.some(seminar =>
                    seminar.SeminarTitle === relatedSeminarFilter
                );
            }

            // Related template filter logic
            let matchesRelatedTemplate = true;
            if (relatedTemplateFilter !== "All") {
                const folderTemplateIds = folder.templates || [];
                const templateTitles = folderTemplateIds.map(templateId => {
                    const template = templateList?.find(t => t._id === templateId);
                    return template ? template.title : null;
                }).filter(title => title);
                matchesRelatedTemplate = templateTitles.includes(relatedTemplateFilter);
            }

            return matchesName && matchesStatus && matchesCreatedDate && matchesCreatedBy && matchesRelatedSeminar && matchesRelatedTemplate;
        });
    }, [list, nameFilter, statusFilter, createdDateFilter, createdByFilter, relatedSeminarFilter, relatedTemplateFilter, userIdToNameMap, templateList]);

    useEffect(() => {
        const filtered = applyFilters();
        setFilteredList(filtered);
        onCountUpdate(filtered.length);

        const currentFilters = {
            name: nameFilter,
            status: statusFilter,
            createdDate: createdDateFilter,
            createdBy: createdByFilter,
            relatedSeminar: relatedSeminarFilter,
            relatedTemplate: relatedTemplateFilter
        };

        // Check if any filter has changed
        const filtersChanged = Object.keys(currentFilters).some(key =>
            currentFilters[key] !== prevFilters[key]
        );

        if (filtersChanged) {
            setCurrentPage(1);
            setPrevFilters(currentFilters);
        }
    }, [list, nameFilter, statusFilter, createdDateFilter, createdByFilter, relatedSeminarFilter, relatedTemplateFilter, userIdToNameMap, applyFilters, onCountUpdate, setCurrentPage, prevFilters]);

    useEffect(() => {
        if (me) {
            eContentService.getFolderList().then((res) => {
                setList(res.data);
            });
        }
    }, [me]);

    const clearFilters = () => {
        setNameFilter("");
        setStatusFilter("All");
        setCreatedDateFilter("All");
        setCreatedByFilter("All");
        setRelatedSeminarFilter("All");
        setRelatedTemplateFilter("All");
    };

    const clearAdvancedFilters = () => {
        setCreatedDateFilter("All");
        setCreatedByFilter("All");
        setRelatedSeminarFilter("All");
        setRelatedTemplateFilter("All");
    };

    const handleOpenFiltersDropdown = () => {
        setIsFiltersDropdownOpen(true);
    };

    const handleCloseFiltersDropdown = () => {
        setIsFiltersDropdownOpen(false);
    };



    const handleEdit = (e, item) => {
        navigateToFolderPage(item);
    }

    const getPermissionClass = (permissions) => {
        if (!permissions) return 'noone';

        // Handle array case
        if (Array.isArray(permissions)) {
            if (permissions.length === 0) return 'noone';
            permissions = permissions[0]; // Take first element
        }

        // Ensure it's a string
        const permissionStr = String(permissions);

        // Convert to lowercase and remove spaces for CSS class
        return permissionStr.toLowerCase().replace(/\s+/g, '');
    }

    const getPermissionDisplay = (permissions) => {
        if (!permissions) return 'No One';

        // Handle array case
        if (Array.isArray(permissions)) {
            if (permissions.length === 0) return 'No One';
            permissions = permissions[0]; // Take first element
        }

        // Ensure it's a string and return as is
        return String(permissions);
    }

    return (
        <div className="FolderList">
            <ListFilter
                filterByName={setNameFilter}
                filterByStatus={setStatusFilter}
                nameFilter={nameFilter}
                statusFilter={statusFilter}
                clearFilters={clearFilters}
                hasAdvancedFilters={true}
                onOpenFiltersModal={handleOpenFiltersDropdown}
                createdDateFilter={createdDateFilter}
                createdByFilter={createdByFilter}
                relatedSeminarFilter={relatedSeminarFilter}
                relatedTemplateFilter={relatedTemplateFilter}
                showStatusFilter={false}
                showNameLabel={false}
                searchIcon={searchIcon}
                advancedFiltersComponent={
                    <AdvancedFiltersDropdown
                        isOpen={isFiltersDropdownOpen}
                        onClose={handleCloseFiltersDropdown}
                        languageFilter="All"
                        onLanguageChange={() => { }}
                        createdDateFilter={createdDateFilter}
                        onCreatedDateChange={setCreatedDateFilter}
                        createdByFilter={createdByFilter}
                        onCreatedByChange={setCreatedByFilter}
                        statusFilter={statusFilter}
                        onStatusChange={setStatusFilter}
                        availableLanguages={[]}
                        availableCreatedDates={availableCreatedDates}
                        availableCreatedBy={availableCreatedBy}
                        onClearAdvancedFilters={clearAdvancedFilters}
                        // Custom filter props for Related Seminar
                        relatedSeminarFilter={relatedSeminarFilter}
                        onRelatedSeminarChange={setRelatedSeminarFilter}
                        availableRelatedSeminars={availableRelatedSeminars}
                        // Custom filter props for Related Template
                        relatedTemplateFilter={relatedTemplateFilter}
                        onRelatedTemplateChange={setRelatedTemplateFilter}
                        availableRelatedTemplates={availableRelatedTemplates}
                    />
                }
            />
            <div className={styles.foldersContainer}>
                {getCurrentPageItems().length > 0 ? getCurrentPageItems().sort((a, b) => a.title.localeCompare(b.title))
                    .map((item) => {
                        return (
                            <div key={item._id} className={styles.folder} onClick={(e) => handleEdit(e, item)}>
                                <div className={styles.left}>
                                    <div className={styles.folderContent}>
                                        <img src={folderIcon} alt="folder" className={styles.folderIcon} />
                                        <div className={styles.folderInfo}>
                                            <div className={styles.folderTitleWrapper}>
                                                <div className={styles.folderTitle}>{item.title}</div>
                                                <div className={`${styles.type} ${styles[getPermissionClass(item.permissions)]}`}>
                                                    {getPermissionDisplay(item.permissions)}
                                                </div>
                                            </div>
                                            <div className={styles.folderItems}>
                                                <div className={styles.items}>{item.contentItems.length} Items</div>
                                                <div className={`${styles.description} ${!item.description ? styles.noDescription : ''}`}>
                                                    {item.description || 'No description'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.right}>
                                    <div className={`${styles.status} ${item.status === 'Active' ? styles.active : styles.suspended}`}>{item.status}</div>
                                </div>
                            </div>
                        )
                    })
                    :
                    <div className={styles.noResultsContainer}>
                        <div className={styles.noResultsText}>No Folders found</div>
                    </div>
                }
            </div>
        </div>
    )
}